import { useRef, useState } from "react";
import { subscribeToKit } from "@/lib/kit";
import { isValidEmail } from "@/lib/utils";

const FORM_ID = "8919245"; // Newsletter Form ID (Kit API v4 numeric form ID)

/**
 * Stable form identity, separate from placement.
 *
 * Every email-capture surface on the site shares this one form, so GA4 sees
 * "newsletter_signup happened 47 times" instead of counting footer, install
 * page, and extension requests as three unrelated forms. The placement still
 * rides along in `form_source` for the per-location breakdown.
 */
const ANALYTICS_FORM_ID = "newsletter_signup";

interface UseNewsletterSubscriptionOptions {
  source?: string;
  productSlug?: string;
  onSuccess?: () => void;
}

export function useNewsletterSubscription({
  source = "install",
  productSlug,
  onSuccess,
}: UseNewsletterSubscriptionOptions = {}) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  // form_start must fire once, on first field focus — not on submit, and not
  // once per keystroke. A ref (not state) so re-renders cannot re-fire it.
  const formStarted = useRef(false);

  const placement = productSlug ? `${source}-${productSlug}` : source;

  const markFormStarted = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    window.trackFormInteraction?.("start", {
      form_id: ANALYTICS_FORM_ID,
      form_source: placement,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setEmailError("");

    setStatus("loading");

    // Build source string for tracking
    const sourceString = productSlug ? `${source}-${productSlug}` : source || "install";

    try {
      await subscribeToKit(FORM_ID, email, undefined, sourceString);

      // Newsletter is audience growth, NOT a sales lead. This used to call
      // window.trackLead, which fired generate_lead plus Meta and X Lead
      // conversions — inflating lead ROAS with people who only wanted
      // product-update emails.
      //
      // form_submit and newsletter_signup are two separate calls on purpose:
      // both globals are optional, and nesting the signup inside the
      // trackFormInteraction guard silently dropped the newsletter event on
      // any surface where the form-interaction tracker had not mounted.
      window.trackFormInteraction?.("submit", {
        form_id: ANALYTICS_FORM_ID,
        form_source: sourceString,
      });

      window.trackNewsletterSignup?.({
        form_id: ANALYTICS_FORM_ID,
        form_source: sourceString,
        // Sent to Google only as a SHA-256 hash (lib/analytics/user-data.ts)
        email,
      });

      setStatus("success");
      setEmail("");
      onSuccess?.();
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setStatus("error");
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError("");
    markFormStarted();
  };

  return {
    email,
    emailError,
    status,
    handleSubmit,
    handleEmailChange,
    /** Wire to the input's onFocus so a tab-in or autofill still counts as start. */
    handleEmailFocus: markFormStarted,
  };
}
